import { Fragment, useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { errText } from "@/lib/api";
import { Panel, Btn, StatusPill, fmt, inputCls, useOps } from "./ui";

function Media({ label, k, testid }) {
  const { api } = useOps();
  if (!k) return <div className="text-[11px] text-slate-600">{label}: —</div>;
  const src = `${api.defaults.baseURL}/media?key=${encodeURIComponent(k)}`;
  return (
    <a href={src} target="_blank" rel="noreferrer" className="block">
      <div className="mb-1 text-[10px] uppercase tracking-wider text-slate-500">{label}</div>
      <img data-testid={testid} src={src} alt={label} className="h-40 w-auto rounded border border-[#243144] object-cover" />
    </a>
  );
}

export function Thread({ messages }) {
  return (
    <div className="space-y-2" data-testid="message-thread">
      {messages.map((m) => (
        <div key={m.id} data-testid={`message-${m.direction}`} className={`max-w-[85%] rounded border px-3 py-2 text-xs ${m.direction === "out" ? "ml-auto border-sky-900 bg-sky-950/40" : "border-[#243144] bg-[#1a2332]"}`}>
          <div className="mb-1 flex items-center gap-2 text-[10px] text-slate-500">
            <span>{m.direction === "out" ? "M →" : "← player"}</span>
            {m.subject && <span className="text-slate-300">{m.subject}</span>}
            <span className="ml-auto"><StatusPill s={m.status} /></span>
          </div>
          <pre className="mono whitespace-pre-wrap break-words text-[12px] leading-5 text-slate-200">{m.body}</pre>
          {(m.attachments || []).length > 0 && <div className="mt-1 text-[10px] text-amber-300">attached: {(m.attachments).map((a) => a.name).join(", ")}</div>}
          <div className="mt-1 text-[10px] text-slate-600">
            {m.direction === "out" ? (m.sent_at ? `sent ${fmt(m.sent_at)}` : `due ${fmt(m.send_at)}`) : fmt(m.created_at)}
            {m.suppressed_reason && ` · ${m.suppressed_reason}`}
          </div>
        </div>
      ))}
    </div>
  );
}

export function StageControl({ player, stages, onDone }) {
  const { api } = useOps();
  const later = stages.slice(stages.indexOf(player.stage) + 1);
  const [stage, setStage] = useState("");
  const go = async () => {
    try { await api.post(`/players/${player.id}/advance`, { stage }); toast.success(`Advanced to ${stage}`); setStage(""); onDone(); }
    catch (e) { toast.error(errText(e)); }
  };
  return (
    <div className="flex items-center gap-2">
      <select data-testid="admin-advance-stage-select" className={`${inputCls} w-auto`} value={stage} onChange={(e) => setStage(e.target.value)}>
        <option value="">advance to…</option>
        {later.map((s) => <option key={s} value={s}>{s}</option>)}
      </select>
      <Btn onClick={go} disabled={!stage} data-testid="admin-advance-stage-button">Advance</Btn>
    </div>
  );
}

export default function PlayerDetail({ id, onChanged }) {
  const { api, setFocusPlayer } = useOps();
  const [d, setD] = useState(null);
  const load = useCallback(() => api.get(`/players/${id}`).then((r) => setD(r.data)).catch(() => setD(null)), [api, id]);
  useEffect(() => { load(); }, [load]);
  if (!d) return null;
  const p = d.player;
  const act = async (fn, ok) => { try { await fn(); toast.success(ok); load(); onChanged?.(); } catch (e) { toast.error(errText(e)); } };
  const del = () => window.confirm("Delete this player and all their media? A final in-world confirmation is sent.") &&
    act(() => api.delete(`/players/${id}`), "Player deleted").then(() => setFocusPlayer(null));

  return (
    <div className="space-y-3" data-testid="admin-player-detail">
      <Panel title={p.serial || "[deleted player]"} actions={<>
        <Btn variant="ghost" onClick={() => act(() => api.post(`/players/${id}/resend-poster`), "Poster queued")} disabled={!p.face_key} data-testid="admin-resend-poster-button">Resend poster</Btn>
        <Btn variant="danger" onClick={del} disabled={!!p.deleted_at} data-testid="admin-delete-player-button">Delete player + media</Btn>
      </>}>
        <div className="grid gap-4 md:grid-cols-2">
          <dl className="grid grid-cols-[110px_1fr] gap-y-1 text-xs">
            {[["Email", p.email], ["Stage", p.stage], ["Source", p.source], ["Referrer", p.referrer_serial], ["Photo", p.photo_status],
              ["Consent", fmt(p.consent_at)], ["18+ confirmed", fmt(p.age_confirmed_at)], ["Opted out", String(p.opted_out)],
              ["Email blocked", p.email_blocked || "—"], ["Flags", (p.flags || []).join(", ") || "—"],
              ["Hints used", JSON.stringify(p.hints_used || {})], ["Nudges", Object.keys(p.nudges_sent || {}).join(", ") || "—"],
              ["Test player", String(!!p.is_test)], ["Created", fmt(p.created_at)]].map(([k, v]) => (
              <Fragment key={k}><dt className="text-slate-500">{k}</dt><dd className="mono text-slate-200 break-all" data-testid={`player-field-${k.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>{v ?? "—"}</dd></Fragment>
            ))}
          </dl>
          <div>
            <div className="mb-1 text-[10px] uppercase tracking-wider text-slate-500">Stage history</div>
            {Object.entries(p.stage_history || {}).map(([s, t]) => <div key={s} className="mono text-[11px] text-slate-300">{s} <span className="text-slate-500">{fmt(t)}</span></div>)}
            <div className="mt-3"><StageControl player={p} stages={d.stages} onDone={load} /></div>
            {(p.flags || []).includes("photo_review") && (
              <div className="mt-3 flex gap-2" data-testid="admin-photo-review">
                <Btn onClick={() => act(() => api.post(`/players/${id}/photo-review`, { approve: true }), "Approved")} data-testid="admin-photo-approve">Approve photo</Btn>
                <Btn variant="danger" onClick={() => act(() => api.post(`/players/${id}/photo-review`, { approve: false }), "Rejected")} data-testid="admin-photo-reject">Reject</Btn>
              </div>
            )}
          </div>
        </div>
        <div className="mt-4 flex flex-wrap gap-4">
          <Media label="Photo" k={p.photo_key} testid="admin-player-photo" />
          <Media label="Face crop" k={p.face_key} testid="admin-player-face" />
          <Media label="Dossier poster" k={p.artifacts?.dossier} testid="admin-player-poster" />
        </div>
      </Panel>
      <Panel title={`M email history (${d.messages.length})`}><Thread messages={d.messages} /></Panel>
    </div>
  );
}

